import { useState, useCallback, useEffect, useRef } from 'react';
import { getServicesSearch } from 'services/services/serviceLists';
import { useDispatch } from 'react-redux';
import { showError } from '../../../../../redux/common/actions';
import { debounce } from 'helpers/utils';

export const useServices = (siteAppID, departmentFilter, timeframeFilter, updateFilter, filterValues) => {
  const dispatch = useDispatch();
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);
  const lastDepartmentId = useRef('');
  const lastTimeframe = useRef({});

  // Fetch services based on search query and other filter values
  const fetchServices = useCallback(async (query = '') => {
    // Prevent multiple simultaneous API calls
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);

      // Build request parameters including other filter values
      const params = {
        siteAppId: siteAppID,
        search: query,
        pageSize: 20,
        pageNumber: 1,
        statusType: "C", // Always filter for completed services (statusType=C)
        sortField: "scheduledDate", // Sort by scheduled date
        sort: "desc",
        chips: filterValues?.chips?.value || [],
      };

      // Add department filter if available
      const departmentValue = departmentFilter?.value;
      if (Array.isArray(departmentValue)) {
        const departmentIds = departmentValue
          .map(dept => parseInt(dept.id, 10))
          .filter(id => !isNaN(id) && id !== 0);
        if (departmentIds.length > 0) {
          params.siteDepartmentIds = departmentIds;
        }
      } else if (departmentValue?.id && departmentValue.id !== '' && departmentValue.id !== 'all') {
        const departmentId = parseInt(departmentValue.id, 10);
        if (!isNaN(departmentId) && departmentId !== 0) {
          params.siteDepartmentIds = [departmentId];
        }
      }

      // Add timeframe filters if available
      if (timeframeFilter?.value) {
        if (timeframeFilter.value.fromDate) {
          params.fromDate = timeframeFilter.value.fromDate;
        }

        if (timeframeFilter.value.toDate) {
          params.toDate = timeframeFilter.value.toDate;
        }
      }

      const response = await getServicesSearch(params);

      if (response.status) {
        const serviceResults = Array.isArray(response.data) ? response.data : [];
        setServices(serviceResults);

        // If the currently selected service is not in the filtered list, clear the selection
        if (filterValues?.service?.value?.id) {
          const serviceExists = serviceResults.some(service => 
            service.id === filterValues.service.value.id
          );

          if (!serviceExists) {
            updateFilter('service', {}, { serviceId: '' });
          }
        }
      } else {
        dispatch(showError(response.data?.message || 'Failed to load services'));
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      dispatch(showError('An error occurred while loading services'));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, siteAppID, departmentFilter, timeframeFilter, updateFilter, isLoading, filterValues]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      fetchServices(query);
    }, 300),
    [fetchServices]
  );

  // Handle search input change
  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
    debouncedSearch(query);
  }, [debouncedSearch]);
  
  // Update data when department or timeframe filters change, but only if dropdown has been used
  useEffect(() => {
    if (!hasInteracted) return;

    // Check if department or timeframe actually changed
    // Handle both single and multiselect department formats
    const departmentValue = departmentFilter?.value;
    let currentDepartmentId;
    if (Array.isArray(departmentValue)) {
      // For multiselect, create a sorted string of IDs
      currentDepartmentId = departmentValue
        .map(dept => dept?.id || '')
        .filter(id => id !== '' && id !== 'all')
        .sort()
        .join(',');
    } else {
      // For single select
      currentDepartmentId = departmentValue?.id || '';
    }

    const currentFromDate = timeframeFilter?.value?.fromDate || '';
    const currentToDate = timeframeFilter?.value?.toDate || '';

    const departmentChanged = currentDepartmentId !== lastDepartmentId.current;
    const timeframeChanged =
      currentFromDate !== lastTimeframe.current.fromDate ||
      currentToDate !== lastTimeframe.current.toDate;

    // Only fetch if something actually changed
    if (departmentChanged || timeframeChanged) {
      // Update refs to current values
      lastDepartmentId.current = currentDepartmentId;
      lastTimeframe.current = {
        fromDate: currentFromDate,
        toDate: currentToDate
      };

      fetchServices(searchQuery);
    }
  }, [departmentFilter, timeframeFilter, hasInteracted, fetchServices, searchQuery]);

  const handleDropdownClick = useCallback(() => {
    setHasInteracted(true);

    // Update refs when manually fetching
    // Handle both single and multiselect department formats
    const departmentValue = departmentFilter?.value;
    if (Array.isArray(departmentValue)) {
      // For multiselect, create a sorted string of IDs
      lastDepartmentId.current = departmentValue
        .map(dept => dept?.id || '')
        .filter(id => id !== '' && id !== 'all')
        .sort()
        .join(',');
    } else {
      // For single select
      lastDepartmentId.current = departmentValue?.id || '';
    }

    lastTimeframe.current = {
      fromDate: timeframeFilter?.value?.fromDate || '',
      toDate: timeframeFilter?.value?.toDate || ''
    };

    return fetchServices(searchQuery);
  }, [departmentFilter, timeframeFilter, fetchServices, searchQuery]);

  return {
    services,
    isLoading,
    searchQuery,
    handleSearchChange,
    handleDropdownClick
  };
}; 
